import { useQuery } from '@tanstack/react-query';

import { SCHOOLY_API_V2_URL } from '../config';
import { CalendarChartData, ChartBarData } from './apiTypes/charts';
import { CalendarChartRequest, ChartBarRequest } from './apiTypes/endpoints/charts';
import { ApiError } from './apiTypes/misc';
import { getAggregatedAssessmentsPath } from './apiTypes/paths/assessments';
import { RQUseQueryOptions } from './apiTypes/query';
import { FilterKeys } from './apiTypes/users';
import { getEventsBaseQueryParams } from './events';
import {
  buildEventsForSchoolAggregateListPath,
  buildSchoolStaffChartListPath,
  buildSchoolStudentsChartListPath,
} from './generated/schooly-api-v1/api-paths';
import * as api from './requests';
import { GetStaffQueryFilters, GetStudentsQueryFilters } from './school';
import { getFormattedParams, joinArrayToString } from './utils/getFormattedParams';
import { removeObjectEmptyArrayValues } from './utils/removeObjectEmptyArrayValues';
import { removeObjectUndefinedNullValues } from './utils/removeObjectUndefinedNullValues';

const SCHOOL_URL = '/school';

export const GET_STUDENT_CHART_QUERY = `${SCHOOL_URL}/GET_STUDENT_CHART_QUERY`;
export const GET_STAFF_CHART_QUERY = `${SCHOOL_URL}/GET_STAFF_CHART_QUERY`;
export const GET_ASSESSMENTS_CHART_QUERY = `${SCHOOL_URL}/GET_ASSESSMENTS_CHART_QUERY`;
export const GET_EVENTS_CHART_QUERY = `${SCHOOL_URL}/GET_EVENTS_CHART_QUERY`;
export const GET_GROUP_CHART_QUERY = `${SCHOOL_URL}/GET_STAFF_GROUP_QUERY`;

function getStudentChart({
  schoolId,
  filters,
  arrange_by,
  breakdown_by,
  query,
}: ChartBarRequest<GetStudentsQueryFilters>): Promise<ChartBarData> {
  const params = removeObjectUndefinedNullValues({
    arrange_by,
    breakdown_by,
    query,

    [FilterKeys.Date]: joinArrayToString(filters?.[FilterKeys.Date]),
    status_ids: joinArrayToString(filters?.[FilterKeys.Status]),
    [FilterKeys.AgeGroup]: joinArrayToString(filters?.[FilterKeys.AgeGroup]),
    [FilterKeys.Nationality]: joinArrayToString(filters?.[FilterKeys.Nationality]),
    [FilterKeys.Gender]: joinArrayToString(filters?.[FilterKeys.Gender]),
    [FilterKeys.House]: joinArrayToString(filters?.[FilterKeys.House]),
    [FilterKeys.Group]: joinArrayToString(filters?.[FilterKeys.Group]),
    [FilterKeys.Expired]: filters?.[FilterKeys.Expired] ? 1 : undefined,
    [FilterKeys.MedicalNeeds]: filters?.[FilterKeys.MedicalNeeds],
    [FilterKeys.CustomFields]: filters?.[FilterKeys.CustomFields]
      ? JSON.stringify(filters?.[FilterKeys.CustomFields])
      : undefined,
  });

  return api.get(buildSchoolStudentsChartListPath({ id: schoolId }), {
    params,
  });
}

function getStaffChart({
  schoolId,
  filters,
  arrange_by,
  breakdown_by,
  query,
}: ChartBarRequest<GetStaffQueryFilters>): Promise<ChartBarData> {
  const params = getFormattedParams(
    {
      arrange_by,
      breakdown_by,
      query,
    },
    filters,
  );

  return api.get(buildSchoolStaffChartListPath({ id: schoolId }), {
    params,
  });
}

export const useGetChartQuery = (
  params: ChartBarRequest<GetStudentsQueryFilters | GetStaffQueryFilters>,
  options?: RQUseQueryOptions<ChartBarData>,
) => {
  const getQueryKey = () => {
    switch (params.entityType) {
      case 'student':
        return GET_STUDENT_CHART_QUERY;
      case 'staff':
        return GET_STAFF_CHART_QUERY;
      default: {
        const _: never = params.entityType;
        return _;
      }
    }
  };
  return useQuery<ChartBarData, ApiError>(
    [getQueryKey(), params],
    async () => {
      switch (params.entityType) {
        case 'student':
          return getStudentChart(params);
        case 'staff':
          return getStaffChart(params);
        default: {
          const _: never = params.entityType;
          return _;
        }
      }
    },
    {
      ...options,
    },
  );
};

export function getAssessmentsCalendarChartData({
  schoolId,
  filters,
}: CalendarChartRequest): Promise<CalendarChartData> {
  const dateFrom = filters?.date?.[0];
  const dateTo = filters?.date?.[1] || filters?.date?.[0];
  const p = filters ? removeObjectEmptyArrayValues(filters) : {};
  const params = {
    assessment_date_from: dateFrom,
    assessment_date_to: dateTo,
    assessment_statuses: p?.assessment_status?.join(','),
    group_ids: p?.group?.join(','),
    subject_ids: p?.subject?.join(','),
    staff_ids: p?.staff?.join(','),
    report_ids: p?.report?.join(','),
    recurrence_ids: p?.recurrence_id?.join(','),
    repetition_types: p?.repetition_type?.join(','),
    area_of_learning_ids: p?.area_of_learning?.join(','),
    evaluation_types: p?.evaluation_type?.join(','),
  };

  return api.get(`${SCHOOLY_API_V2_URL}${getAggregatedAssessmentsPath({ school_id: schoolId })}`, {
    params,
  });
}

export function getEventsCalendarChartData({
  schoolId,
  filters,
}: CalendarChartRequest): Promise<CalendarChartData> {
  const params = filters ? getEventsBaseQueryParams(filters) : {};

  return api.get(buildEventsForSchoolAggregateListPath({ school_id: schoolId }), {
    params,
  });
}

export const useGetAssessmentChartQuery = (
  params: CalendarChartRequest,
  options?: RQUseQueryOptions<CalendarChartData>,
) => {
  return useQuery<CalendarChartData, ApiError>(
    [GET_ASSESSMENTS_CHART_QUERY, params],
    () => getAssessmentsCalendarChartData(params),
    {
      ...options,
    },
  );
};

export const useGetEventsChartQuery = (
  params: CalendarChartRequest,
  options?: RQUseQueryOptions<CalendarChartData>,
) => {
  return useQuery<CalendarChartData, ApiError>(
    [GET_EVENTS_CHART_QUERY, params],
    () => getEventsCalendarChartData(params),
    {
      ...options,
    },
  );
};
